import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { useBreakpoint, useClickOutside, useFocusTrap } from '@pharmac-extranet/shared'
import {
  PharmacColumn,
  PharmacH3,
  PharmacIcon,
  PharmacMegaMenu,
  PharmacRow,
  PharmacUpGradientBackground,
} from '@pharmac/ui'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { useNavigationBar } from './NavigationBarContext'
import { NavigationLink } from './useExtranetLinks'

type OnFocusProps = React.PropsWithChildren &
  React.HTMLAttributes<HTMLDivElement> & {
    onMouseLeave: () => void
  }

const FocusTrapRoot = styled.div``

export const MegaMenuFocusTrap = ({ children, onMouseLeave, style }: OnFocusProps) => {
  const [hasFocus, setHasFocus] = React.useState(true)

  const [nodeRef] = useClickOutside(onMouseLeave)

  useFocusTrap(hasFocus, onMouseLeave, 400)

  return (
    <FocusTrapRoot
      ref={nodeRef}
      style={style}
      onMouseEnter={() => setHasFocus(true)}
      onMouseLeave={() => setHasFocus(false)}
    >
      {children}
    </FocusTrapRoot>
  )
}

const MenuListItemHeightPx = 36

const MenuList = styled.ul`
  padding: 0;
  margin: 0;
  bottom: 0;
  box-sizing: border-box;
  width: auto;
  list-style-type: none;
  min-height: ${4 * MenuListItemHeightPx}px;
`
const MenuHeader = styled.div`
  display: flex;
  flex-direction: row;
  padding: var(--spacing-large) 0;
  color: var(--color-primary);
  height: 20px;
  align-items: flex-end;
  gap: var(--spacing-small);
`

const SubmenuItemStyles = css`
  list-style-type: none;
  border-radius: var(--border-radius-tiny);
  align-items: center;
  user-select: none;
  box-sizing: border-box;
  user-select: none;

  margin: var(--spacing-small) 0;

  button,
  a {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;

    padding: 0 var(--spacing-small);

    height: ${MenuListItemHeightPx}px;
    outline: 0;
    border: 0;
    text-decoration: none;

    color: var(--color-primary);
    font-family: var(--font-family-primary);
    font-size: var(--font-size-normal);
    font-weight: 600;
    background-color: transparent;

    width: 100%;
    text-align: left;

    &:hover {
    }

    svg {
      margin-left: auto;
    }
  }
`

const MainMenuItem = styled.li<{ isSelected?: boolean }>`
  ${SubmenuItemStyles};

  background-color: ${({ isSelected }) =>
    isSelected ? 'var(--color-primary)' : 'var(--extranet-light-purple)'};

  &:hover {
    background-color: var(--color-primary);

    a,
    button {
      color: var(--color-white);
      cursor: pointer;
    }
  }

  a,
  button {
    color: ${({ isSelected }) => (isSelected ? 'var(--color-white)' : 'var(--color-primary)')};
  }
`
export const SubMenuItem = styled.li<{ isSelected?: boolean }>`
  ${SubmenuItemStyles};

  a,
  button {
    font-weight: 600;
  }

  &:hover {
    a,
    button {
      font-weight: 700;
    }
  }
`

type MegaSubMenuProps = {
  route: NavigationLink
  depthIndex: number
}

type MegaSubmenuLinkProps = {
  link: NavigationLink
  depthIndex: number
}

const MegaSubMenuLink: React.FC<MegaSubmenuLinkProps> = ({ link, depthIndex }) => {
  const { openMegaMenu, megaMenu, clearMegaMenu, removeLastMenu } = useNavigationBar()

  const hasSublinks = !!link.children && link.children.length > 0

  const LinkComponent = depthIndex > 0 ? SubMenuItem : MainMenuItem

  if (hasSublinks) {
    const isSelected = megaMenu.routes.indexOf(link) === depthIndex + 1

    return (
      <LinkComponent {...{ isSelected }}>
        <button onMouseOver={() => openMegaMenu(link, depthIndex + 1)}>
          <span>{link.label}</span>
          <PharmacIcon
            icon="chevronRight"
            size="17"
          />
        </button>
      </LinkComponent>
    )
  }

  const onMouseEnter = () => {
    if (megaMenu.routes.length > depthIndex + 1) {
      removeLastMenu()
    }
  }

  return (
    <LinkComponent onMouseEnter={onMouseEnter}>
      <NavLink
        to={link.path as string}
        onClick={clearMegaMenu}
      >
        {link.label}
      </NavLink>
    </LinkComponent>
  )
}

const MegaSubMenu: React.FC<MegaSubMenuProps> = ({ route, depthIndex }) => {
  if (!route.children) return null

  const k = (index: number) => 'mm' + depthIndex + '-' + index

  return (
    <MenuList>
      {route.children.map((sublink, index) => (
        <MegaSubMenuLink
          key={k(index)}
          depthIndex={depthIndex}
          link={sublink}
        />
      ))}
    </MenuList>
  )
}

const Wrapper = styled.div`
  margin: 0 auto;
  width: 1200px;
  padding-top: var(--spacing-large);
  padding-bottom: var(--spacing-medium);
`

export const MegaMenu = () => {
  const { megaMenu, clearMegaMenu } = useNavigationBar()

  const { routes, isOpen } = megaMenu

  const { t } = useTranslation()

  const breakpoint = useBreakpoint()

  if (breakpoint.maxMobile) {
    return null
  }

  return (
    <PharmacMegaMenu isOpen={isOpen}>
      <PharmacUpGradientBackground>
        <MegaMenuFocusTrap onMouseLeave={clearMegaMenu}>
          <Wrapper>
            <PharmacRow>
              <PharmacColumn xs={12}>
                <MenuHeader>
                  <PharmacH3 color="purple">
                    {!!routes[0] && t(megaMenu.routes[0].label as unknown as TemplateStringsArray)}
                  </PharmacH3>
                </MenuHeader>
              </PharmacColumn>
              <>
                {routes.map((route, depth) => (
                  <PharmacColumn
                    xs={4}
                    md={3}
                    lg={3}
                    key={'mmsm-' + depth}
                  >
                    <MegaSubMenu
                      route={route}
                      depthIndex={depth}
                    />
                  </PharmacColumn>
                ))}
              </>
            </PharmacRow>
          </Wrapper>
        </MegaMenuFocusTrap>
      </PharmacUpGradientBackground>
    </PharmacMegaMenu>
  )
}
